import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X, Loader2, ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import ServiceCard from '@/components/ServiceCard';
import SearchBar from '@/components/SearchBar';
import { FadeIn } from '@/components/Animations';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '@/components/ui/collapsible';
import {
  RadioGroup,
  RadioGroupItem
} from "@/components/ui/radio-group";
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusinessParameters } from '@/hooks/useParameters';
import { useServices } from '@/hooks/useServices';
import { useCategories } from '@/hooks/useCategories';
import { useCategoryGroups } from '@/hooks/useCategoryGroups';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslatedDocumentTitle } from '@/hooks/useDocumentTitle';
import { prepareServiceForCard } from '@communitly/shared';

// Define default service logo to use as fallback
const defaultServiceLogo = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1000';

const ServiceCardSkeleton = () => (
  <div className="bg-card rounded-lg overflow-hidden shadow-sm border border-border animate-pulse">
    <div className="h-48 bg-muted"></div>
    <div className="p-4 space-y-3">
      <div className="h-5 bg-muted rounded w-3/4"></div>
      <div className="h-4 bg-muted rounded w-1/2"></div>
      <div className="h-4 bg-muted rounded w-1/4"></div>
    </div>
  </div>
);

const Services = () => {
  const { t } = useTranslation();
  const { currentCommunity } = useCommunity();
  const { defaultBannerUrl } = useBusinessParameters();
  const { data: categoryData } = useCategories();
  const { data: categoryGroups, isLoading: categoryGroupsLoading } = useCategoryGroups();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Infinite scroll refs
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  
  // Get search query from URL parameters
  const searchTerm = searchParams.get('search') || '';
  const categoryParam = searchParams.get('category');
  
  // Fetch services based on selected category with infinite scrolling
  const { 
    data, 
    isLoading: servicesLoading, 
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useServices(selectedCategory, undefined, { infinite: true, pageSize: 12 });

  // Set page title using the new hook with proper error handling
  useTranslatedDocumentTitle('services.title');

  // Initialize selected category from URL parameter
  useEffect(() => {
    if (categoryParam && categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam);
      
      if (categoryData?.categories) {
        const categoryName = categoryData.categories.find(c => c.id === categoryParam)?.name || 'Selected category';
        toast({
          description: `Showing services in ${categoryName}`,
        });
      }
    }
  }, [categoryParam, categoryData, selectedCategory]);

  // Handle search input change
  const handleSearchChange = (query: string) => {
    if (query) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('search', query);
        return newParams;
      });
    } else {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
    }
  };

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    if (categoryId === '') {
      // Selected "All categories"
      setSelectedCategory(null);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('category');
        return newParams;
      });
      toast({
        description: t('services.categories.all_selected'),
      });
    } else if (selectedCategory === categoryId) {
      // Deselect category
      setSelectedCategory(null);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('category');
        return newParams;
      });
      toast({
        description: t('services.categories.all_selected'),
      });
    } else {
      // Select new category
      setSelectedCategory(categoryId);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('category', categoryId);
        return newParams;
      });
      
    }
  };

  // Intersection Observer callback for infinite scroll
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Set up intersection observer
  useEffect(() => {
    const currentLoadMoreRef = loadMoreRef.current;
    
    if (currentLoadMoreRef) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        rootMargin: '100px', // Trigger 100px before the element comes into view
        threshold: 0.1
      });
      
      observerRef.current.observe(currentLoadMoreRef);
    }

    return () => {
      if (observerRef.current && currentLoadMoreRef) {
        observerRef.current.unobserve(currentLoadMoreRef);
      }
    };
  }, [handleIntersection]);

  // Filter services based on search term
  const filteredServices = React.useMemo(() => {
    const allServices = data?.pages.flatMap(page => page.services) || [];
    if (!allServices.length) return [];
    
    if (!searchTerm) return allServices;
    
    return allServices.filter(service => {
      const name = service.name || service.title || '';
      const description = service.description || '';
      
      return (
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [data?.pages, searchTerm]);

  return (
    <Layout>
      {/* Mobile-optimized title section */}
      <div className="lg:hidden max-w-6xl mx-auto px-4 py-8">
        <div className="mb-3">
          <h1 className="text-2xl font-bold mb-2">
            {t('services.page.title', { community: currentCommunity.name })}
          </h1>
          <p className="text-sm text-muted-foreground hidden">
            {t('services.page.subtitle')}
          </p>
        </div>
      </div>

      {/* Desktop hero section with gradient */}
      <section className="hidden lg:block relative py-12 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="py-4"
          >
            <div className="mb-3 lg:mb-6">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 text-gray-900">
                {t('services.page.title', { community: currentCommunity.name })}
              </h1>
              <p className="text-sm md:text-base mb-2 lg:mb-4 text-gray-600 hidden lg:block">
                {t('services.page.subtitle')}
              </p>
            </div>
            <div className="w-full max-w-xl">
              <div
                className="h-1 rounded-full mb-2"
                style={{
                  backgroundImage: `linear-gradient(to right, ${currentCommunity.primary_color}, ${currentCommunity.accent_color})`
                }}
                role="presentation">
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-2 md:py-4 bg-white">
        <FadeIn direction="up" delay={0.1} className="mb-4 md:mb-6">
          {/* Custom SearchBar with Filter icon instead of magnifying glass */}
          <div className="relative">
            <form onSubmit={(e) => { e.preventDefault(); }} className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-6 pr-16 py-4 rounded-full text-base placeholder:text-muted-foreground/60 bg-white border border-gray-200 focus:ring-2 focus:ring-primary/30 focus:ring-offset-0 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none"
                placeholder={t('services.search.placeholder')}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-14 flex items-center pr-2"
                >
                  <X className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                <div className="bg-gray-100 text-gray-600 md:bg-primary md:text-white w-10 h-10 rounded-full flex items-center justify-center border border-gray-200 md:border-0 hover:bg-gray-200 md:hover:bg-primary hover:text-gray-700 md:hover:text-white md:shadow-sm md:hover:shadow-md transition-all duration-300 hover:scale-105">
                  <Filter className="h-5 w-5" />
                </div>
              </button>
            </form>
          </div>

          {/* Search results info */}
          {searchTerm && (
            <div className="mt-4 text-sm">
              <span className="text-muted-foreground">{t('services.search.results')} </span>
              <span className="font-medium">"{searchTerm}"</span>
              <button 
                onClick={() => handleSearchChange('')}
                className="ml-2 text-primary hover:underline"
              >
                {t('services.search.clear')}
              </button>
            </div>
          )}
        </FadeIn>

        {/* Mobile Filter Dropdown */}
        {isFilterOpen && (
          <div className="md:hidden mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-4">                      
                  <div>
                    <h3 className="font-medium mb-2">{t('services.categories.title')}</h3>
                    {!categoryGroupsLoading && categoryGroups ? (
                      <div className="max-h-[300px] overflow-y-auto pr-2">
                        <RadioGroup 
                          value={selectedCategory || ""}
                          onValueChange={(value) => handleCategorySelect(value)}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="" id="all-categories" />
                            <label htmlFor="all-categories" className="text-sm cursor-pointer">
                              {t('services.categories.all')}
                            </label>
                          </div>
                          
                          {categoryGroups.categoryGroups?.map((group) => (
                            <div key={group.id} className="mb-4">
                              <h4 className="text-sm font-medium text-muted-foreground mb-2">{group.name}</h4>
                              <div className="space-y-2 ml-2">
                                {group.categories?.map((category) => (
                                  <div key={category.id} className="flex items-center space-x-2">
                                    <RadioGroupItem value={category.id} id={category.id} />
                                    <label htmlFor={category.id} className="text-sm cursor-pointer">
                                      {category.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Array(5).fill(0).map((_, i) => (
                          <Skeleton key={i} className="h-6 w-full" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid md:grid-cols-[280px,1fr] gap-4 md:gap-6">
          
          {/* Desktop Filter Sidebar */}
          <div className="hidden md:block">
            <div className="sticky top-24 space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">{t('services.categories.title')}</h3>
                      {!categoryGroupsLoading && categoryGroups ? (
                        <div className="max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                          <RadioGroup 
                            value={selectedCategory || ""}
                            onValueChange={(value) => handleCategorySelect(value)}
                          >
                            <div className="flex items-center space-x-2 mb-2">
                              <RadioGroupItem value="" id="all-categories-desktop" />
                              <label htmlFor="all-categories-desktop" className="text-sm cursor-pointer">
                                {t('services.categories.all')}
                              </label>
                            </div>
                            
                            {categoryGroups.categoryGroups?.map((group) => (
                              <div key={group.id} className="mb-4">
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">{group.name}</h4>
                                <div className="space-y-2 ml-2">
                                  {group.categories?.map((category) => (
                                    <div key={category.id} className="flex items-center space-x-2">
                                      <RadioGroupItem value={category.id} id={`${category.id}-desktop`} />
                                      <label htmlFor={`${category.id}-desktop`} className="text-sm cursor-pointer">
                                        {category.name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {Array(8).fill(0).map((_, i) => (
                            <Skeleton key={i} className="h-6 w-full" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Services Grid */}
          <div>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
                <strong className="font-bold">{t('services.error')}</strong>
                <span className="block sm:inline ml-2">{error.message}</span>
              </div>
            )}

            {servicesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {Array(6).fill(0).map((_, i) => (
                  <ServiceCardSkeleton key={i} />
                ))}
              </div>
            ) : filteredServices.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  {filteredServices.map((service, index) => {
                    const location = service.location || 'Unknown location';
                    // For service cards: company_banner_url → company_logo → defaultBannerUrl
                    const imageUrl = service.companyBannerUrl || service.companyLogo || defaultBannerUrl;
                    return (
                      <ServiceCard
                        key={service.id}
                        name={service.name || service.title || t('service.card.unnamed')}
                        id={service.id}
                        slug={service.slug}
                        category={service.category || ''}
                        imageUrl={imageUrl}
                        rating={service.rating || 0}
                        reviewCount={service.reviewCount || 0}
                        location={location}
                        featured={service.featured || service.isfeatured || false}
                        discount={service.discount}
                        delay={0} // Remove stagger delay for infinite scroll
                        is_active={service.is_active}
                        is_listed={service.is_listed}
                        opening_hours={service.opening_hours}
                      />
                    );
                  })}
                </div>

                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="py-4">
                  {isFetchingNextPage && (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mr-2" />
                      <span className="text-muted-foreground">
                        {t('services.loading_more', { defaultValue: 'Loading more services...' })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Load more button (fallback for accessibility) */}
                {hasNextPage && !isFetchingNextPage && (
                  <div className="flex justify-center pt-6">
                    <Button 
                      onClick={() => fetchNextPage()}
                      variant="outline"
                      className="group"
                      disabled={isFetchingNextPage}
                    >
                      {t('services.load_more', { defaultValue: 'Load More Services' })}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                )}

                {/* End of list message */}
                {!hasNextPage && filteredServices.length > 0 && !servicesLoading && (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      {t('services.end_of_list', { 
                        defaultValue: 'You\'ve reached the end of all services.' 
                      })}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">{t('services.no_results')}</p>
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchParams({});
                  }}
                  className="mt-4 text-primary hover:underline"
                >
                  {t('services.reset_filters')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Services;
